import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MathCaptcha, useCaptcha } from "@/components/MathCaptcha";
import { API } from "@/lib/apiBase";

const inputCls = "bg-transparent border-white/10 text-white/90 font-body text-sm placeholder:text-white/20 h-11 rounded-sm focus:border-merlot/50";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const captcha = useCaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email");
    if (!captcha.answer) return toast.error("Please answer the security question");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, origin_url: window.location.origin, ...captcha.payload });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      captcha.refresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24 pb-20 min-h-screen flex items-center justify-center" data-testid="forgot-password-page">
      <div className="w-full max-w-sm px-6">
        <div className="text-center mb-10">
          <h1 className="font-display text-2xl text-white/90 mb-1">Forgot your password?</h1>
          <p className="font-body text-sm text-white/40">Enter your email and we'll send you a reset link.</p>
        </div>
        {sent ? (
          <div className="glass-card rounded-sm p-6 text-center" data-testid="forgot-password-sent">
            <MailCheck className="w-8 h-8 text-gold mx-auto mb-3" />
            <p className="font-body text-sm text-white/80">If an account exists for <span className="text-white">{email}</span>, a reset link is on its way. It expires in 1 hour.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="font-body text-xs text-white/40 tracking-wider uppercase block mb-2">Email</label>
              <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className={inputCls} />
            </div>
            <MathCaptcha captcha={captcha} testPrefix="forgot-captcha" />
            <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full bg-merlot hover:bg-merlot-light text-white font-body text-sm h-11 rounded-sm">
              {loading ? "Sending…" : "Send Reset Link"}
            </Button>
          </form>
        )}
        <p className="font-body text-sm text-white/30 text-center mt-8">
          <Link to="/login" className="text-merlot-light hover:text-gold transition-colors" data-testid="forgot-back-to-login">Back to sign in</Link>
        </p>
      </div>
    </main>
  );
}
